// src/config/layersConfig.js

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "https://my-backend-api-80u5.onrender.com";

/**
 * Configuration for all layers in the application.
 * Supports both predefined and dynamic mapId layers.
 */
const layersConfig = [
  // Updated Layer: Forest Loss Range (uses persistent mapId)
    // New Layer: Global Deforestation Alerts (Direct from GFW)
{
  id: "forest-loss-range",
  name: "Forest Loss 2001 - 2023",
  category: "Forest Change",
  type: "custom",
  component: "ForestLossRangeComponent", // reference to your custom React component
    subLayers: [
      {
        id: "forest-loss-range-main",
        type: "raster",
        isVisible: true,
        getMapId: async (signal, yearRange) => {
          const { start, end } = yearRange;
          console.log("Fetching forest loss for:", { start, end });

          // Build query string
          const params = new URLSearchParams({
            startYear: start.toString(),
            endYear: end.toString(),
          });

          // AbortController signal is optional, but recommended to handle cancellation
          const resp = await fetch(`/api/mapid-forest-loss-range?${params}`, {
            signal,
          });

          if (!resp.ok) {
            console.error("Failed to fetch forest loss mapId:", await resp.text());
            throw new Error("Failed to fetch forest loss mapId");
          }

          // The serverless function returns { mapid, token }
          const { mapid, token } = await resp.json();
          console.log("Got mapid:", mapid, "token:", token);

          // Return the Earth Engine tile URL
          return `https://earthengine.googleapis.com/v1/${mapid}/tiles/{z}/{x}/{y}${
            token ? `?token=${token}` : ""
          }`;
        },
      },
    ],

  // Initial year range if you want it in config
  yearRange: {
    start: 2001,
    end: 2023,
  },
  opacity: 100,
  isVisible: true,
  function: "Visualize forest loss in selected year range.",
  coverage: "Global",
source1: `Original data available
  <a href="https://storage.googleapis.com/earthenginepartners-hansen/GFC-2023-v1.11/download.html"
     target="_blank"
     rel="noopener noreferrer">here</a>.
  Accessed through Google Earth Engine.<br>
  University of Maryland (UMD)<br>
  Google<br>
  United States Geological Survey (USGS)<br>
  National Aeronautics and Space Administration (NASA)<br>`,
  methodology: `<p>The dataset was created using multispectral satellite imagery from three sources: Landsat 5 Thematic Mapper, Landsat 7 Enhanced Thematic Mapper Plus, and Landsat 8 Operational Land Imager sensors. Images captured during the growing season were selected for their suitability in mapping land cover. In total, over 1 million images were processed—more than 600,000 Landsat 7 images covering the 2000–2012 period and 400,000 images from Landsat 5, 7, and 8 for the 2011–2021 period.
The images underwent several pre-processing steps:</p>
    <ul>
      <li>Resolution Adjustment: Pixel values were recalculated if the image resolution was altered.</li>
      <li>Screening: Images were examined and filtered to remove those affected by clouds, shadows, and water.</li>
      <li>Calibration and Normalization: All images were calibrated and normalized to ensure they were comparable.</li>
    </ul>
    <p>To identify tree cover loss on a per-pixel basis, land surface observations were compiled and analyzed using a supervised learning algorithm. This approach relied on labeled data to train a machine learning model. The processing was conducted continent by continent—North America, South America, Eurasia, Africa, and Australia—using Google Earth Engine, a cloud platform that integrates a vast public data catalog with high-powered computational resources for geospatial analysis.
Since its initial creation, the dataset has been updated annually to include data up to 2021. Enhancements to the algorithm—such as the incorporation of new data, reprocessing of data from 2011 onward, and improved modeling and calibration techniques—have led to more accurate detection of forest loss. This includes better identification of losses due to boreal fires, smallholder rotational agriculture in tropical forests, selective logging, and short-cycle plantations.</p>`,
  timeRange: "2001-2023",
  updateFrequency: "Annual",
  resolution: "30m (Landsat-based)",
  credits: "<p>Hansen, M. C., P. V. Potapov, R. Moore, M. Hancher, S. A. Turubanova, A. Tyukavina, D. Thau, S. V. Stehman, S. J. Goetz, T. R. Loveland, A. Kommareddy, A. Egorov, L. Chini, C. O. Justice, and J. R. G. Townshend. 2013. “High-Resolution Global Maps of 21st-Century Forest Cover Change.” Science 342 (15 November): 850–53. Data available online.</p>",
  cautions: "<p>May include some commission/omission errors in tropical regions.</p>",
  Licence: "<p>Creative Commons - Attribution 4.0 International</p>",

  description: "Visualize forest loss for a selected year range (2001-2023).",
  legend: {
    type: "gradient",
    colors: [
  "#FCE9E9", // 2001
  "#FADCDC", // 2002
  "#F8CECE", // 2003
  "#F6C1C1", // 2004
  "#F5B4B4", // 2005
  "#F2A7A7", // 2006
  "#F19A9A", // 2007
  "#EF8D8D", // 2008
  "#ED8080", // 2009
  "#EA7272", // 2010
  "#E96565", // 2011
  "#E65858", // 2012
  "#E44C4C", // 2013
  "#E23E3E", // 2014
  "#E03232", // 2015
  "#DE2525", // 2016
  "#D32020", // 2017
  "#C61E1E", // 2018
  "#B91D1D", // 2019
  "#AB1B1B", // 2020
  "#9E1919", // 2021
  "#911717", // 2022
  "#841515"  // 2023
]
,
    labels: ["2001", "2023"],
    title: "Forest Loss by Year",
    minYear: 2001,
    maxYear: 2023,
    numLabels: 2
  }
},

  // Updated Layer: Canopy Height (uses persistent mapId)
{
  id: "canopy-height",
  name: "Tree Cover Height",
  category: "Forest Change",
  type: "custom",  // Change type to custom
  component: "CanopyHeightComponent", // Reference to your new component
  isVisible: true,
  function: "Shows estimated canopy height globally.",
  coverage: "Global",
  source1: "Accessed through Google Earth Engine - Community Catalog.\n" +
    "Meta and World Resources Institude 2023",
  methodology: `
    <p>Analyzing satellite imagery from 2009 to 2020—with a special emphasis on data from 2018 to 2020—this dataset offers extensive temporal coverage to monitor changes in canopy height across the planet’s landmass. Utilizing AI models such as DiNOv2, it predicts canopy height with a mean absolute error of just 2.8 meters, enabling precise assessments of carbon stocks and the effectiveness of mitigation strategies.
</p>
  `,
  timeRange: "Most recent composite (2019-2021)",
  updateFrequency: "Irregular",
  resolution: "~25m (varies by source)",
  credits: "<p>Tolan, J., Yang, H.I., Nosarzewski, B., Couairon, G., Vo, H.V., Brandt, J., Spore, J., Majumdar, S., Haziza, D., Vamaraju, J. and Moutakanni, T., 2024. Very high resolution canopy height maps from RGB imagery using self-supervised vision transformer and convolutional decoder trained on aerial lidar. Remote Sensing of Environment, 300, p.113888.</p>",
  cautions: "<p>May underestimate height in areas of dense canopy overlap.</p>",
  Licence: "<p>Creative Commons - Attribution 4.0 International</p>",
  description: "Global canopy height visualization",
  opacity: 100,
  legend: {
    type: "gradient",
    colors: ["#440154", "#3b528b", "#21908c", "#5dc963", "#fde725"],
    labels: ["0m", "25m"],
    title: "Canopy Height (m)",
    min: 0,
    max: 25,
    numLabels: 2,
  },
},
{
  id: "deforestation-alerts-malaysia",
  name: "Deforestation Alerts",
  category: "Forest Change",
  type: "custom", // Using custom type for the DeforestationComponent

  component: "DeforestationComponent", // Reference to the custom component
  subLayers: [
    {
      id: "deforestation-alerts-malaysia-main",
      isVisible: true,
      getMapId: async (signal, dateRange) => {
        // Log details for debugging
        console.log("Fetching deforestation alerts with dates:", dateRange);

        // Format dates for URL parameters
        const startDate = dateRange?.startDate || "2022-01-01";
        const endDate = dateRange?.endDate || new Date().toISOString().split("T")[0];

        // Build URL with parameters for the new GFW Integrated Alerts endpoint
        // Using 'latest' version to always get the most recent data
        const baseUrl = `https://tiles.globalforestwatch.org/gfw_integrated_alerts/latest/dynamic/{z}/{x}/{y}.png`;

        // Add query parameters for filtering
        const queryParams = new URLSearchParams({
          start_date: startDate,
          end_date: endDate,
          render_type: "encoded", // Use encoded rendering for classification
          alert_confidence: "low" // Include all confidence levels
        }).toString();

        // Final tile URL with query params
        const tileUrl = `${baseUrl}?${queryParams}`;

        // Log the final URL for debugging
        console.log("Using GFW Integrated Alerts tile URL:", tileUrl);

        return tileUrl;
      },
      source: {
        tiles: [""] // This will be populated by getMapId
      }
    }
  ],
  dateRange: {
    startDate: "2022-01-01",
    endDate: new Date().toISOString().split("T")[0] // Default to today
  },
  dynamicMapId: true,
  visibility: "none",
  isVisible: true,
  function: "Displays near-real-time deforestation alerts with user-defined date filtering.",
  coverage: "Global",
  source1: "GLAD Alerts:<br>" +
      "Hansen, M.C., A. Krylov, A. Tyukavina, P.V. Potapov, S. Turubanova, B. Zutta, S. Ifo, B. Margono, F. Stolle, and R. Moore. 2016. Humid tropical forest disturbance alerts using Landsat data. Environmental Research Letters, 11 (3). (https://dx.doi.org/10.1088/1748-9326/11/3/034008)<br><br>" +
      "GLAD-S2 Alerts:<br>" +
      "Pickens, A.H., Hansen, M.C., Adusei, B., and Potapov P. 2020. Sentinel-2 Forest Loss Alert. Global Land Analysis and Discovery (GLAD), University of Maryland.<br><br>" +
      "RADD Alerts:<br>" +
      "Reiche, J., Mullissa, A., Slagter, B., Gou, Y., Tsendbazar, N.E., Braun, C., Vollrath, A., Weisse, M.J., Stolle, F., Pickens, A., Clinton, N., Donchyts, G., Gorelick, N., Herold, M. 2021. Forest disturbance alerts for the Congo Basin using Sentinel-1. Environmental Research Letters. (https://doi.org/10.1088/1748-9326/abd0a8)"
    ,
  methodology: `<p>
  Global Forest Watch has assembled this dataset by integrating deforestation alerts from three distinct alert systems—GLAD-L, GLAD-S2, and RADD—into a single, unified deforestation alert layer. This integration enables users to detect deforestation events more rapidly than relying on any individual system, as the consolidated layer updates whenever any source alert system is refreshed.
  <br><br>
  The source alert systems are derived from satellites with varying spectral and spatial resolutions. Specifically, 30-meter GLAD Landsat-based alerts are up-sampled to align with the 10-meter spatial resolution of the Sentinel-based alerts (GLAD-S2 and RADD). This approach prevents the double counting of overlapping alerts, which are instead highlighted with darker pixels to indicate higher confidence.
  <br><br>
  Alerts are classified as high confidence when detected twice by a single system, a scenario that may occur in areas or during times when only one alert system is operational. In regions where multiple alert systems are active, alerts detected by two or all three systems are classified as having the highest confidence. With several sensors confirming changes at the same location, the probability of false positives is reduced, allowing for more rapid and reliable alerting without waiting for additional satellite imagery.
</p>`,
  timeRange: "2022 - Present",
  updateFrequency: "Weekly",
  resolution: "30m",
  credits: "<p>Integrated Deforestation Alerts\". UMD/GLAD and WUR, accessed through Global Forest Watch</p>",
  cautions: "<p>Alerts are preliminary and may include some false positives.</p>",
  Licence: "<p>Creative Commons - Attribution 4.0 International.</p>",
  description: "Displays deforestation alerts with varying confidence levels and supports custom date filtering.",
  opacity: 100,
  legend: {
    type: "classed-color",
    colors: ["#EDA4C3", "#DC6699", "#C92A6D"],
    labels: [
      "Detected by a single alert system",
      "High Confidence: detected more than once",
      "Highest Confidence: detected by multiple systems"
    ],
    title: "Deforestation Alerts Confidence"
  },
  isNew: false,
  isUpdated: false,
  thumbnail: null
},
{
  id: "logging-concessions",
  name: "Logging Concessions",
  category: "Land Use",
  type: "custom",  // Changed from 'geojson' to 'custom'
  subLayers: [
    {
      id: "logging-concessions-a",
      type: "fill",
      // ... other layer-specific properties
    },
    {
      id: "logging-concessions-a-outline",
      type: "line",
      // ... other layer-specific properties
    },
  ],
  component: "LoggingConcessionsComponent", // Add this line
  isVisible: true,
  function: "Shows known logging concession areas worldwide.",
  coverage: "Global (where data is available)",
  source1: "Global Forest Watch concession data",
  methodology: `
    <p>Aggregated from official government data on active logging concessions.
    May not include unauthorized concessions.</p>
  `,
  timeRange: "Varies by region",
  updateFrequency: "Irregular (as data is updated)",
  resolution: "N/A (vector data)",
  credits: "<p>Credits: GFW, official government data sources</p>",
  cautions: "<p>Boundaries may be approximate or outdated.</p>",
  description: "Displays global logging concessions managed by Global Forest Watch.",
  opacity: 50,
  legend: {
    type: "classed-color",
    colors: ["rgb(255, 219, 143)"],
    labels: ["Logging Concessions"],
    title: "Logging Concessions",
    minYear: null,
    maxYear: null,
  }
},
  // Existing Layer: Oil Palm Concessions (remains unchanged)
  {
  id: "oil-palm-concessions",
  name: "Oil Palm Concessions",
  category: "Land Use",
  type: "custom",  // Now handled as a custom vector layer
  component: "OilPalmConcessionsComponent", // New component for vector rendering
  source: {
    type: "vector",
    url: "mapbox://gfw_oil_palm_v20191031-11u8ed"
  },
  // Define subLayers or layer-specific properties (e.g., type: "fill" or "line") as needed
  visibility: "none",
  isVisible: true,
  function: "Shows areas allocated for oil palm plantations.",
  coverage: "Global (with emphasis on tropical regions)",
    source1: "Various government and NGO datasets (via Mapbox style)",
    methodology: `
      <p>Aggregates known concession boundaries from multiple sources,
      visualized in a custom Mapbox style.</p>
    `,
    timeRange: "Most recent data available",
    updateFrequency: "Irregular",
    resolution: "~ (source-specific)",
    credits: "<p>Credits: Various local authorities, Mapbox</p>",
    cautions: "<p>Some polygons may be incomplete or unverified.</p>",
    description: "Displays oil palm concession areas and their distribution",
    opacity: 50,
    legend: {
      type: "dot",
      colors: ["#0ba83a"],
      labels: ["Oil Palm Concession"],
      title: "Oil Palm Concession",
      minYear: null,
      maxYear: null,
      numLabels: 5,
    },
    isNew: false,
    isUpdated: false,
    thumbnail: null,
    dynamicMapId: false,
  },

  // Existing Layer: Global Land Cover 2020 (remains unchanged)
  {
    id: "landcover2020",
    name: "Land Cover CCI",
    category: "Land Cover",
    type: "custom",  // Changed to custom
    component: "LandcoverComponent", // Add this line
    visibility: "none",
    isVisible: true,
    function: "Displays ESA CCI Land Cover classification for 2020.",
    coverage: "Global",
    source1: "© ESA Climate Change Initiative - Land Cover led by UCLouvain (2017)",
    methodology: `<p>
      Version 2.07 of this dataset was developed under the European Space Agency's Climate Change Initiative (CCI), which aims to generate long-term, consistent global data for climate modeling. The CCI Land Cover project provides annual, global land cover maps at a 300-meter resolution from 1992 to 2015, though Global Forest Watch currently displays only the 2015 map.
      </p>
      <p>
      To maintain year-to-year consistency, each year's land cover map is derived from a single baseline map. This baseline was generated using the complete set of MERIS images from 2003 to 2012, employing both unsupervised classification and a machine learning algorithm applied over multiple years of imagery. Subsequent changes are detected at a 1 km resolution by comparing annual data from different sensors: AVHRR data for 1992 to 1999, SPOT-VGT data for 1999 to 2013, and PROVA-V data for 2014 and 2015. For a change to be counted, it must persist for two consecutive years, except for forest changes in 2014 and 2015, which are considered reliably detected. These 1 km resolution changes are then merged with the baseline map and refined to a 300-meter resolution starting from 2004, when MERIS and PROVA-V data became available.
      </p>
      <p>
      The final dataset categorizes global land cover into 22 classes. For easier visualization on Global Forest Watch, these are consolidated into simplified categories based on IPCC definitions (agriculture, forest, grassland, wetland, settlement, shrubland, sparse vegetation, bare area, water, and permanent ice and snow). The complete set of classes and annual land cover maps dating back to 1992 can be accessed via the ESA/CCI viewer.
      </p>`,
    timeRange: "2020",
    updateFrequency: "Annual (CCI updates)",
    resolution: "300m",
    credits: `<p>
    Zanaga, D., Van De Kerchove, R., De Keersmaecker, W., Souverijns, N., Brockmann, C., Quast, R., Wevers, J., Grosu, A., Paccini, A., Vergnaud, S., Cartus, O., Santoro, M., Fritz, S., Georgieva, I., Lesiv, M., Carter, S., Herold, M., Li, Linlin, Tsendbazar, N.E., Ramoino, F., Arino, O., 2021. ESA WorldCover 10 m 2020 v100. (doi:10.5281/zenodo.5571936)
    </p>
    <p>
    Zanaga, D., Van De Kerchove, R., Daems, D., De Keersmaecker, W., Brockmann, C., Kirches, G., Wevers, J., Cartus, O., Santoro, M., Fritz, S., Lesiv, M., Herold, M., Tsendbazar, N.E., Xu, P., Ramoino, F., Arino, O., 2022. ESA WorldCover 10 m 2021 v200.
    </p>`,
    cautions: "<p>Some classes may be generalized at 300m scale.</p>",
    Licence: "<p>Creative Commons - Attribution 4.0 International</p>",
    description: "ESA CCI Land Cover 2020",
    opacity: 100,
    legend: {
    type: "classed-color",
    shape: "square",
    title: "Land Cover 2020 Legend",
      colors: [
    "#FFFF64", // 10: Cropland, rainfed
    "#FFEB64", // 11: - Herbaceous cover
    "#FFFF00", // 12: - Tree or shrub cover
    "#AAF0F0", // 20: Cropland irrigated or post-flooding
    "#DCF064", // 30: Mosaic cropland (>50%) / natural vegetation (<50%)
    "#C8C864", // 40: Mosaic natural vegetation (>50%) / cropland (<50%)
    "#006400", // 50: Tree cover, broadleaved, evergreen, closed to open (>15%)
    "#00A000", // 60: Tree cover, broadleaved, deciduous, closed to open (>15%)
    "#00B000", // 61: - Tree cover, broadleaved, deciduous, closed (>40%)
    "#AAC800", // 62: - Tree cover, broadleaved, deciduous, open (15-40%)
    "#003C00", // 70: Tree cover, needleleaved, evergreen, closed to open (>15%)
    "#004C00", // 71: - Tree cover, needleleaved, evergreen, closed (>40%)
    "#005000", // 72: - Tree cover, needleleaved, evergreen, open (15-40%)
    "#285000", // 80: Tree cover, needleleaved, deciduous, closed to open (>15%)
    "#296000", // 81: - Tree cover, needleleaved, deciduous, closed (>40%)
    "#286400", // 82: - Tree cover, needleleaved, deciduous, open (15-40%)
    "#788200", // 90: Tree cover, mixed leaf type (broadleaved and needleleaved)
    "#8CA000", // 100: Mosaic T and shrub (>50%) / herbaceous cover (<50%)
    "#BE9600", // 110: Mosaic herbaceous cover (>50%) / T and shrub (<50%)
    "#966400", // 120: Shrubland
    "#FFDCD2", // 121: - Shrubland evergreen
    "#FFEBAF", // 122: - Shrubland deciduous
    "#FFB432", // 130: Grassland
    "#FFD278", // 140: Lichens and mosses
    "#FFEBAF", // 150: Sparse vegetation (<15%)
    "#FFEBAF", // 151: - Sparse tree (<15%)
    "#FFD278", // 152: - Sparse shrub (<15%)
    "#FFEBAF", // 153: - Sparse herbaceous cover (<15%)
    "#00785A", // 160: Tree cover, flooded, fresh or brackish water
    "#009678", // 170: Tree cover, flooded, saline water
    "#00DC82", // 180: Shrub or herbaceous cover, flooded, fresh/saline/brakish water
    "#C31400", // 190: Urban areas
    "#FFF5D7", // 200: Bare areas
    "#DCDCDC", // 201: - Consolidated bare areas
    "#FFF5D7", // 202: - Unconsolidated bare areas
    "#0046C8", // 210: Water bodies
    "#FFFFFF",  // 220: Permanent snow and ice
    "#404040", // 0: No Data

  ],
  labels: [
    "Cropland, rainfed",
    "- Herbaceous cover",
    "- Tree or shrub cover",
    "Cropland irrigated or post-flooding",
    "Mosaic cropland (>50%) / natural vegetation (<50%)",
    "Mosaic natural vegetation (>50%) / cropland (<50%)",
    "Tree cover, broadleaved, evergreen, closed to open (>15%)",
    "Tree cover, broadleaved, deciduous, closed to open (>15%)",
    "- Tree cover, broadleaved, deciduous, closed (>40%)",
    "- Tree cover, broadleaved, deciduous, open (15-40%)",
    "Tree cover, needleleaved, evergreen, closed to open (>15%)",
    "- Tree cover, needleleaved, evergreen, closed (>40%)",
    "- Tree cover, needleleaved, evergreen, open (15-40%)",
    "Tree cover, needleleaved, deciduous, closed to open (>15%)",
    "- Tree cover, needleleaved, deciduous, closed (>40%)",
    "- Tree cover, needleleaved, deciduous, open (15-40%)",
    "Tree cover, mixed leaf type (broadleaved and needleleaved)",
    "Mosaic T and shrub (>50%) / herbaceous cover (<50%)",
    "Mosaic herbaceous cover (>50%) / T and shrub (<50%)",
    "Shrubland",
    "- Shrubland evergreen",
    "- Shrubland deciduous",
    "Grassland",
    "Lichens and mosses",
    "Sparse vegetation (<15%)",
    "- Sparse tree (<15%)",
    "- Sparse shrub (<15%)",
    "- Sparse herbaceous cover (<15%)",
    "Tree cover, flooded, fresh or brackish water",
    "Tree cover, flooded, saline water",
    "Shrub or herbaceous cover, flooded, fresh/saline/brakish water",
    "Urban areas",
    "Bare areas",
    "- Consolidated bare areas",
    "- Unconsolidated bare areas",
    "Water bodies",
    "Permanent snow and ice",
    "No Data"
  ],
    minYear: null,
    maxYear: null,
  },
    isNew: false,
    isUpdated: false,
    thumbnail: null,
    dynamicMapId: false,
  },

  // Existing Layer: Sarawak Licenses for Planted Forests (remains unchanged)
{
  id: "sarawak-lpf",
  name: "Licenses for Planted Forests",
  category: "Land Use",
  type: "custom",  // Custom layer: handled by a React component
    subLayers: [
      {
        id: "sarawak-lpf-fill",
        type: "fill",
        // ... other layer-specific properties
      },
      {
        id: "sarawak-lpf-outline",
        type: "line",
        // ... other layer-specific properties
      },
    ],
  component: "SarawakLPFComponent", // Name of the component to render this layer
  isVisible: true,
  function: "Shows the known Licenses for Planted Forests (LPFs) in Sarawak.",
  coverage: "Sarawak, Malaysia",
  source1: "Official Sarawak Government data (where available)",
  methodology: `
    <p>Vector polygons representing licensed plantation areas within Sarawak.
    Derived from official or semi-official sources.</p>
  `,
  timeRange: "Varies by concession",
  updateFrequency: "Irregular",
  resolution: "N/A (vector data)",
  credits: "<p>Credits: Sarawak Forestry Department, local NGOs</p>",
  cautions: "<p>Not all licensed areas may be shown or updated.</p>",
  description: "Displays boundaries of known Licenses for Planted Forests (LPFs) within Sarawak, Malaysia.",
  opacity: 50,
  legend: {
    type: "classed-color",
    colors: ["rgba(210,10,50,0.6)"],
    labels: ["Sarawak Licenses"],
    title: "Sarawak LPF",
    minYear: null,
    maxYear: null,
  },
  isNew: false,
  isUpdated: false,
  thumbnail: null,
  dynamicMapId: false,
},

  // Existing Layer: Sarawak Protected Areas (remains unchanged)
   {
    id: "sarawak_protected_areas",
    name: "Protected Areas",
    category: "Land Use",
    type: "custom",  // Custom layer: handled by a React component
  subLayers: [
    { id: "sarawak_protected_areas-fill", type: "fill", },
    { id: "sarawak_protected_areas-line", type: "line",  },
    // Add other sublayers as needed
  ],
    component: "SarawakProtectedAreasComponent", // Name of the component to render this layer
    isVisible: true,
    function: "Highlights protected areas (National Parks, Wildlife Sanctuaries, Reserves) in Sarawak.",
    coverage: "Sarawak, Malaysia",
    source1: "Sarawak Forestry Department, local authorities",
    methodology: `
      <p>Polygon boundaries of official and proposed Totally Protected Areas (TPAs) within Sarawak,
      as declared by local statutes.</p>
    `,
    timeRange: "Varies by each protected area declaration",
    updateFrequency: "Irregular",
    resolution: "N/A (vector data)",
    credits: "<p>Credits: Sarawak Forestry Department, local conservation NGOs</p>",
    cautions: "<p>Boundaries may change with new gazettements or degazettements.</p>",
    description: "Shows the boundaries of current and proposed Totally Protected Areas (National Parks, Wildlife Sanctuaries, Nature Reserves).",
    opacity: 50,
    legend: {
      type: "classed-color",
      colors: ["rgba(49, 130, 189, 0.75)"],
      labels: ["Sarawak Protected Areas"],
      title: "Sarawak Protected Areas",
      minYear: null,
      maxYear: null,

  },
    isNew: false,
    isUpdated: false,
    thumbnail: null,
    dynamicMapId: false,
  },
{
  id: "biomass-layer-id",
  name: "Aboveground Biomass",
  category: "Carbon",
  type: "custom",
  component: "BiomassComponent",
  visibility: "visible",
  opacity: 100,
  methodology: `
    <p> This dataset provides estimates of forest above-ground biomass (AGB) for the years 2010, 2015, 2016, 2017, 2018, 2019, 2020, and 2021. The AGB estimates are derived from various Earth observation sources, which vary by year, including data from the Copernicus Sentinel-1 mission, Envisat’s ASAR instrument, and JAXA’s Advanced Land Observing Satellites (ALOS-1 and ALOS-2), along with additional Earth observation data. This product is part of the European Space Agency's (ESA's) Climate Change Initiative (CCI) programme, developed by the Biomass CCI team.
    </p>
    <p>
    This release, version 5, includes updates and new products compared to version 4. Specifically, maps for 2010, 2017, 2018, 2019, and 2020 have been updated, and new maps for 2015, 2016, and 2021 have been added. Additionally, new AGB change maps have been generated for consecutive years (2015-2016, 2016-2017, and 2020-2021) as well as updated change maps for the intervals 2010-2020, 2017-2018, 2018-2019, and 2019-2020, plus a decadal change map (2020-2010). The remote sensing data pool now includes multi-temporal L-band observations for all biomes and years. The AGB maps have been improved by incorporating revised allometric equations based on an extended record of spaceborne LiDAR data from the GEDI and ICESat-2 missions. Temporal dynamics are now integrated into the retrieval algorithm to better capture biomass changes, and biases between 2010 and later years have been reduced.
    </p>
    <p>
    The data products include two global raster layers:<br>
    <strong>1- Above-Ground Biomass (AGB):</strong> Expressed in tons per hectare (Mg/ha), this represents the oven-dry weight of the woody components (stem, bark, branches, and twigs) of all living trees, excluding stumps and roots.<br>
    <strong>2- AGB Uncertainty:</strong> Per-pixel estimates of uncertainty expressed as the standard deviation (Mg/ha).
    </p>
    <p>
    In addition, new aggregated data products are available at coarser spatial resolutions (1, 10, 25, and 50 km) for both the AGB and AGB change layers.
    </p>
    <p>
    Files detailing AGB change between consecutive years (2015-2016, 2016-2017, 2017-2018, 2018-2019, 2019-2020, and 2020-2021) and over a decade (2020-2010) are also provided. These files, labeled accordingly (e.g., 2015_2016, 2016_2017, etc.), contain two sets of maps: one showing the standard deviation of the AGB change and another indicating a quality flag for the AGB change. Note that the actual AGB change is not provided directly, as it can be computed by subtracting the relevant AGB maps.
    </p>`,
  timeRange: "2010-2020",
  updateFrequency: "Annual",
  resolution: "100m",
  credits: "<p>Santoro, M.; Cartus, O. (2024): ESA Biomass Climate Change Initiative (Biomass_cci): Global datasets of forest above-ground biomass for the years 2010, 2015, 2016, 2017, 2018, 2019, 2020 and 2021, v5.01. NERC EDS Centre for Environmental Data Analysis, 22 August 2024. doi:10.5285/bf535053562141c6bb7ad831f5998d77. https://dx.doi.org/10.5285/bf535053562141c6bb7ad831f5998d77</p>",
  source1: "© ESA Climate Change Initiative - Biomass led and managed by Professor Richard Lucas (University of Aberystwyth) and the Science Lead is Professor Shaun Quegan (Sheffield University)",
  Licence: "<p>Creative Commons - Attribution 4.0 International</p>",
  cautions: "<p>Uncertainty varies by region and forest type. Includes per-pixel uncertainty estimates.</p>",
  description: "Global forest above-ground biomass estimates derived from multiple satellite missions, measuring woody biomass in tons/hectare.",
  function: "Provides critical data for forest carbon stock assessment, deforestation monitoring, and climate change studies.",
  coverage: "Global",
  citation: "Santoro, M.; Cartus, O. (2023): ESA Biomass Climate Change Initiative (Biomass_cci): Global datasets of forest above-ground biomass for the years 2010, 2017, 2018, 2019 and 2020, v4. NERC EDS Centre for Environmental Data Analysis.",
  doi: "10.5285/af60720c1e404a9e9d2c145d2b2ead4e",
  legend: {
    type: "gradient",
    colors: ['#FAFCE0', '#D0D8B2', '#A6B688', '#7A9660', '#4E763C', '#32653C', '#18543B',
      '#004336', '#003941', '#002D4D', '#001D4F', '#10003F'],
    labels: ["1Mg/ha", "450Mg/ha"],
    title: "Above Ground Biomass",
    min: 1,
    max: 450,
    numLabels: 2,
  },
  subLayers: [
     {
      id: "biomass-2020",
      year: 2020,
      isVisible: true,
      getMapId: async (signal) => {
        const resp = await fetch(`/api/mapid-biomass?year=2020`, { signal });
        if (!resp.ok) throw new Error(`Failed to fetch biomass 2020 mapid`);
        const { mapid, token } = await resp.json();
        return `https://earthengine.googleapis.com/v1/${mapid}/tiles/{z}/{x}/{y}${token ? `?token=${token}` : ""}`;
      },
      source: { tiles: [""] },
    },

    {
      id: "biomass-2017",
      year: 2017,
      isVisible: false,
      getMapId: async (signal) => {
        const resp = await fetch(`/api/mapid-biomass?year=2017`, { signal });
        if (!resp.ok) throw new Error(`Failed to fetch biomass 2017 mapid`);
        const { mapid, token } = await resp.json();
        return `https://earthengine.googleapis.com/v1/${mapid}/tiles/{z}/{x}/{y}${token ? `?token=${token}` : ""}`;
      },
      source: { tiles: [""] },
    },
    {
      id: "biomass-2018",
      year: 2018,
      isVisible: false,
      getMapId: async (signal) => {
        const resp = await fetch(`/api/mapid-biomass?year=2018`, { signal });
        if (!resp.ok) throw new Error(`Failed to fetch biomass 2018 mapid`);
        const { mapid, token } = await resp.json();
        return `https://earthengine.googleapis.com/v1/${mapid}/tiles/{z}/{x}/{y}${token ? `?token=${token}` : ""}`;
      },
      source: { tiles: [""] },
    },
    {
      id: "biomass-2019",
      year: 2019,
      isVisible: false,
      getMapId: async (signal) => {
        const resp = await fetch(`/api/mapid-biomass?year=2019`, { signal });
        if (!resp.ok) throw new Error(`Failed to fetch biomass 2019 mapid`);
        const { mapid, token } = await resp.json();
        return `https://earthengine.googleapis.com/v1/${mapid}/tiles/{z}/{x}/{y}${token ? `?token=${token}` : ""}`;
      },
      source: { tiles: [""] },
    },
      {
      id: "biomass-2010",
      year: 2010,
      isVisible: false,
      getMapId: async (signal) => {
        const resp = await fetch(`/api/mapid-biomass?year=2010`, { signal });
        if (!resp.ok) throw new Error(`Failed to fetch biomass 2010 mapid`);
        const { mapid, token } = await resp.json();
        return `https://earthengine.googleapis.com/v1/${mapid}/tiles/{z}/{x}/{y}${token ? `?token=${token}` : ""}`;
      },
      source: { tiles: [""] },
    },

  ],
}
  // ... other layer configurations if needed
];




/**
 * Helper function to interpolate colors between two hex codes.
 * Generates a specified number of colors forming a gradient.
 */
function interpolateColors(numColors, startColor, endColor) {
  const hexToRgb = (hex) => {
    const bigint = parseInt(hex.slice(1), 16);
    return [
      (bigint >> 16) & 255,
      (bigint >> 8) & 255,
      bigint & 255,
    ];
  };

  const rgbToHex = (rgb) =>
    "#" +
    rgb
      .map((c) => {
        const hex = c.toString(16);
        return hex.length === 1 ? "0" + hex : hex;
      })
      .join("");

  const startRgb = hexToRgb(startColor);
  const endRgb = hexToRgb(endColor);

  const gradient = [];
  for (let i = 0; i < numColors; i++) {
    const ratio = i / (numColors - 1);
    const interpolatedRgb = startRgb.map((start, idx) =>
      Math.round(start + (endRgb[idx] - start) * ratio)
    );
    gradient.push(rgbToHex(interpolatedRgb));
  }

  return gradient;
}

export default layersConfig;
